import type { Variant } from 'types';
import type { WeightEntry } from 'helpers/weight-distribution';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganizations } from '@/state/useOrganizations';
import { useClient } from 'hooks/useClient';

type UpdateVariantParams = { id: number; name?: string; redirectUrl?: string | null };

/** What PUT /variants/:id accepts for a Server-side experiment. null clears a field. */
export type VariantFieldsUpdate = { key?: string | null; payload?: unknown };

export const useVariants = (experimentId: number) => {
  const queryClient = useQueryClient();
  const apiClient = useClient();
  const { organization } = useOrganizations();

  // Explicit generic: apiClient.get's return type collapses to `any` (it casts
  // to `T | any`), which would otherwise leak into every consumer of `variants`.
  const { data: variants, isPending: variantsPending } = useQuery<Variant[]>({
    queryKey: ['VARIANTS', experimentId],
    enabled: !!experimentId,
    queryFn: () => apiClient.get<Variant[]>(`/experiments/${experimentId}/variants`),
  });

  /**
   * Throws rather than returning: the bare `return` made "Add Variant" close its dialog
   * and add nothing, which reads as the variant having been created.
   */
  const createVariant = async (variant: any) => {
    if (!organization) {
      throw new Error('Select a client in the header before adding a variant.');
    }
    if (!variant.name?.trim()) {
      throw new Error('Give the variant a name before adding it.');
    }
    const { id: companyId, parentCompanyId } = organization;
    const payload = { ...variant, companyId, parentCompanyId };
    await apiClient.post(`/variants`, payload);
    queryClient.invalidateQueries({ queryKey: ['VARIANTS', experimentId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
  };

  const deleteVariant = async (variantId: number) => {
    await apiClient.delete(`/variants/${variantId}`);
    queryClient.invalidateQueries({ queryKey: ['VARIANTS', experimentId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
  };

  /**
   * Only the fields passed are written; on a running experiment a new destination goes
   * live on save. Refetches even when it throws: a failed publish (502) still saved.
   */
  const updateVariant = async ({ id, name, redirectUrl }: UpdateVariantParams) => {
    try {
      await apiClient.put(`/variants/${id}`, { name, redirectUrl });
    } finally {
      queryClient.invalidateQueries({ queryKey: ['VARIANTS', experimentId] });
      queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
      queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
    }
  };

  /** Throws on a blank name so the dialog can show it and stay open. */
  const duplicateVariant = async (variantId: number, name: string) => {
    if (!name.trim()) {
      throw new Error('Give the copy a name before duplicating.');
    }
    await apiClient.post(`/variants/${variantId}/duplicate`, { name: name.trim() });
    queryClient.invalidateQueries({ queryKey: ['VARIANTS', experimentId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
  };

  /**
   * A Server-side experiment variant's SDK fields: the key customer code switches on (Draft only)
   * and its JSON payload (at most 8 KB). Throws the API's `{ status, message }` on a
   * refusal, a 409 for a key another variant already uses, so the dialog can stay open.
   */
  const updateVariantFields = async (variantId: number, fields: VariantFieldsUpdate) => {
    await apiClient.put(`/variants/${variantId}`, fields);
    queryClient.invalidateQueries({ queryKey: ['VARIANTS', experimentId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
  };

  // Whole distribution or nothing: the API rejects partial writes so the stored
  // weights always sum to 100%.
  const setWeights = async (weights: WeightEntry[]) => {
    await apiClient.put(`/experiments/${experimentId}/weights`, { weights });
    queryClient.invalidateQueries({ queryKey: ['VARIANTS', experimentId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
  };

  const setControlVariant = async (variantId: number) => {
    await apiClient.post(`/experiments/${experimentId}/set-control-variant`, { variantId });
    queryClient.invalidateQueries({ queryKey: ['VARIANTS', experimentId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT'] });
    queryClient.invalidateQueries({ queryKey: ['ACTIVITY_LOG', experimentId] });
    queryClient.invalidateQueries({ queryKey: ['EXPERIMENT_CONTROL_VARIANT', experimentId] });
  };

  return {
    variants,
    variantsPending,
    createVariant,
    updateVariant,
    duplicateVariant,
    updateVariantFields,
    setWeights,
    deleteVariant,
    setControlVariant,
  };
};
